import type { BrandOnboarding, BrandSalesChannel } from '@fluenceur/types';

/**
 * Source of truth for the brand onboarding v3 (mock-up `Brand Onboarding v3`,
 * the v2 for every step it does not change): the five steps in order, the route of each, and which one comes next. The
 * progress bars, the side panel, every step's navigation and the
 * `SubscriptionGuard` redirect of a free organization all read this module, so
 * the order lives in one place.
 */
export const ONBOARDING_STEPS = ['brand', 'creators', 'collabs', 'sales', 'connect'] as const;

export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number];

/** `welcome` closes the onboarding: every step is behind it, none ahead. */
export type OnboardingStep = OnboardingStepKey | 'welcome';

export const ONBOARDING_STEP_COUNT = ONBOARDING_STEPS.length;

export const ONBOARDING_PATHS: Record<OnboardingStep, string> = {
  brand: '/onboarding/brand',
  creators: '/onboarding/creators',
  collabs: '/onboarding/collaborations',
  sales: '/onboarding/sales',
  connect: '/onboarding/connect',
  welcome: '/onboarding/welcome',
};

/** 0-based index of the active step; `welcome` returns the step count (all done). */
export const activeStepIndex = (step: OnboardingStep): number =>
  step === 'welcome' ? ONBOARDING_STEP_COUNT : ONBOARDING_STEPS.indexOf(step);

/** A brand with no online store has nothing to connect: it skips step 4. */
export const stepAfterSales = (salesChannel: BrandSalesChannel): OnboardingStep =>
  salesChannel === 'none' ? 'welcome' : 'connect';

/** The step "Back" returns to, or null on the first step. */
export function previousStep(
  step: OnboardingStep,
  salesChannel: BrandSalesChannel | null
): OnboardingStep | null {
  if (step === 'welcome') return salesChannel === 'none' ? 'sales' : 'connect';

  const index = ONBOARDING_STEPS.indexOf(step);
  return index > 0 ? ONBOARDING_STEPS[index - 1] : null;
}

/**
 * Where an organization that has not finished its onboarding picks it up, read
 * from the answers the server holds. Steps 1 and 2 leave no answer the session
 * carries (the name is already set by the sign-up, the creator preferences live
 * with the Companion), so a brand without collaborations starts at step 1
 * again, its name and its preferences prefilled.
 *
 * `isStoreConnected` is whether the store of the chosen channel is connected;
 * a store that came back connected from its install has nothing left to do on
 * step 4.
 */
export function nextOnboardingStep(
  onboarding: BrandOnboarding | undefined,
  isStoreConnected: boolean
): OnboardingStep {
  if (!onboarding || onboarding.collaborationTypes.length === 0) return 'brand';
  if (!onboarding.salesChannel) return 'sales';
  if (onboarding.salesChannel === 'none' || isStoreConnected) return 'welcome';
  return 'connect';
}

/**
 * Whether the store of a sales channel is connected. Only Shopify can be
 * connected today: the Stripe and API connections are not built yet.
 */
export const isSalesChannelConnected = (
  salesChannel: BrandSalesChannel | null | undefined,
  isShopifyConnected: boolean
): boolean => salesChannel === 'shopify' && isShopifyConnected;
